import React, {useCallback, useEffect, useState} from 'react';
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useIndustry from "../../../../../../hooks/use-industry";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import Grid2 from "@mui/material/Unstable_Grid2";
import {Box, Card, Checkbox, Fade, FormControlLabel, IconButton, MenuItem, Paper, Skeleton, TextField, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {DragHandleRounded, OpenInNew, WarningOutlined} from "@mui/icons-material";
import {Link} from "react-router-dom";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import {ReactSortable} from "react-sortablejs";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import axios from "axios";
import Utils from "../../../../../../../core/services/utils/utils";
import ChangeOrderDialog from "../../../../../../components/dialogs/change-order";
import Api from "../../../../../../../core/services/api/api";
import {PanelFooter} from "../../../../../../components/layout";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const initChangeOrderDialogState = {
    open: false,
    close: () => void 0,
    orderIndex: 0,
    length: 0,
    reorder: () => void 0,
    message: '',
}

const ProductPropertiesConfigurationsView = () => {
    const [loadingProperties, setLoadingProperties] = useState(true);
    const [properties, setProperties] = useState([]);
    const [settingHighlighted, setSettingHighlighted] = useState([]);
    const [moving, setMoving] = useState(false);
    const [changeOrderDialogState, setChangeOrderDialogState] = useState(initChangeOrderDialogState);
    const {industries, hasNoIndustries, loadingIndustries, cachedIndustry, fetchedIndustry, fetchIndustry} = useIndustry({noCategories: true});
    const [waitingForCommitting, setWaitingForCommitting] = useState({
        orders: false,
        highlighted: false,
    });
    const isMounted = useIsMounted();
    const highlightedNamesRef = React.useRef([]);

    const {configureNotebookTags} = useConfigureNotebook('product-properties-configurations-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        if (!cachedIndustry?.id)
            return;
        void fetchIndustry({industryId: cachedIndustry.id});
    }, [cachedIndustry?.id, fetchIndustry]);

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.settingsProductPropertyManagement);
        configureBehavioralTool((tags) => tags.settingsProductPropertyManagement);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the properties of the industry with the given id.
     * @type {(function(*, *): Promise<void>)}
     */
    const getIndustryProperties = useCallback(async (industryId, axiosCancelToken) => {
        highlightedNamesRef.current = [];
        setProperties([]);
        setLoadingProperties(true);
        const response = await Api.getIndustryPropertyExtraConfigs(industryId, axiosCancelToken);
        if (!isMounted())
            return;
        if (!response?.aborted) {
            setLoadingProperties(false);
        }
        if (response.resultFlag) {
            const items = response.data?.industryProperties?.map((e) => ({
                ...e,
                isHighlighted: response.data.highlightedProperties?.includes(e.name) ?? false,
            })) ?? [];
            const ordered = [];
            for (const id of response.data.propertiesOrders) {
                const found = items.find((item) => item.id === id);
                if (found)
                    ordered.push(found);
            }
            const rest = items.filter((item) => !ordered.includes(item));
            rest.sort((a, b) => a.title.localeCompare(b.title));
            ordered.push(...rest);
            setProperties(ordered.map((e, i) => ({...e, orderIndex: i})));
            highlightedNamesRef.current = ordered.filter((e) => e.isHighlighted).map((e) => e.name);
        }
    }, [isMounted])

    /**
     * Fetches the properties of the industry with the given id.
     * - Automatically fetches the properties of the industry when the fetched industry changes.
     */
    useEffect(() => {
        if (!fetchedIndustry?.id)
            return;
        const axiosCancelToken = axios.CancelToken.source();
        void getIndustryProperties(fetchedIndustry.id, axiosCancelToken.token);
        return () => axiosCancelToken.cancel();
    }, [fetchedIndustry?.id, getIndustryProperties]);

    /**
     * Toggles the highlighted status of the property with the given id.
     * @type {(function(*): Promise<void>)}
     */
    const toggleHighlighted = useCallback(async (property) => {
        setProperties(prevState => prevState.map((e) => e.id === property.id ? {...e, isHighlighted: !e.isHighlighted} : e));
    }, [])

    /**
     * Applies the highlighted status of the properties with the given names.
     * @type {(function(*): Promise<void>)}
     */
    const applyHighlighted = useCallback(async (names) => {
        setSettingHighlighted(names);
        const forApi = {
            industryId: fetchedIndustry?.id,
            highlightedProperties: names,
            variants: [],
            updatedProperties: ['HighlightedProperties'],
        }
        const response = await Api.saveIndustryPropertyExtraConfigs(forApi)
        if (!isMounted())
            return;
        setSettingHighlighted([]);
        setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
        if (response?.resultFlag) {
            setProperties(prevState => prevState.map((e) => ({
                ...e,
                isHighlighted: names.includes(e.name),
            })));
            highlightedNamesRef.current = names;
        }
    }, [fetchedIndustry?.id, isMounted])

    /**
     * Automatically applies the highlighted status of the properties when the properties change.
     * - This is to ensure that the highlighted status of the properties is applied after 5 seconds of the last change.
     */
    useEffect(() => {
        if (!properties.length) {
            setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
            return;
        }
        const names = properties.filter((e) => e.isHighlighted).map((e) => e.name);
        const newSet = new Set(names);
        const committedSet = new Set(highlightedNamesRef.current);
        const notSame = names.length !== highlightedNamesRef.current.length || names.some((e) => !committedSet.has(e)) || highlightedNamesRef.current.some((e) => !newSet.has(e));
        if (!notSame) {
            setWaitingForCommitting(prevState => ({...prevState, highlighted: false}));
            return;
        }
        setWaitingForCommitting(prevState => ({...prevState, highlighted: true}));
        const timer = setTimeout(() => {
            void applyHighlighted(names)
        }, 3000);
        return () => clearTimeout(timer);
    }, [applyHighlighted, properties])

    /**
     * Toggle the order of the properties.
     * @type {function(*): Promise<void>}
     */
    const toggleOrder = useCallback(async (properties) => setProperties(properties), [])

    /**
     * Applies the order of the properties.
     * @type {(function(*): Promise<void>)}
     */
    const applyOrder = useCallback(async (orderIds) => {
        setMoving(true);
        const forApi = {
            industryId: fetchedIndustry?.id,
            propertiesOrders: orderIds,
            variants: [],
            updatedProperties: ['PropertiesOrders'],
        }
        const response = await Api.saveIndustryPropertyExtraConfigs(forApi)
        if (!isMounted())
            return;
        setMoving(false);
        setWaitingForCommitting(prevState => ({...prevState, orders: false}));
        if (response?.resultFlag) {
            setProperties(prevState => {
                // Reorder the properties based on the new order.
                const ordered = [];
                for (const id of orderIds) {
                    const found = prevState.find((item) => item.id === id);
                    if (found) {
                        ordered.push(found);
                    }
                }
                return ordered.map((e, i) => ({...e, orderIndex: i}));
            });
        } else {
            setProperties(prevState => {
                const newState = [...prevState];
                newState.sort((a, b) => a.orderIndex - b.orderIndex);
                return newState;
            })
        }
    }, [fetchedIndustry?.id, isMounted])

    /**
     * Applies the order of the properties when the properties change.
     * - This is to ensure that the order of the properties is applied after 3 seconds of the last change.
     */
    useEffect(() => {
        if (!properties.length) {
            setWaitingForCommitting(prevState => ({...prevState, orders: false}));
            return;
        }
        const orderIds = properties.map((e) => e.id);
        const committedProperties = [...properties]
        committedProperties.sort((a, b) => a.orderIndex - b.orderIndex);
        const committedOrderIds = committedProperties.map((e) => e.id);
        if (orderIds.join(',') === committedOrderIds.join(',')) {
            setWaitingForCommitting(prevState => ({...prevState, orders: false}));
            return;
        }
        setWaitingForCommitting(prevState => ({...prevState, orders: true}));
        const timer = setTimeout(() => {
            void applyOrder(orderIds)
        }, 3000);
        return () => clearTimeout(timer);
    }, [applyOrder, properties])

    /**
     * Opens the dialog for changing the order of the property.
     * @type {(function(*): void)}
     */
    const openReorderDialog = useCallback((oldIndex) => {
        setChangeOrderDialogState({
            ...initChangeOrderDialogState,
            open: true,
            close: () => setChangeOrderDialogState(initChangeOrderDialogState),
            orderIndex: oldIndex + 1,
            length: properties.length,
            reorder: (newIndex) => {
                setProperties(prevState => Utils.reorderList(prevState, oldIndex, newIndex - 1));
                return {resultFlag: true};
            },
            message: 'Please select the new order of the property. This will change the order of the property in the list. and shift the other properties accordingly.',
        });
    }, [properties.length])

    return (
        <>
            {
                loadingIndustries &&
                <LoadingLottieAnimation width={'80%'}/>
            }
            {
                !loadingIndustries &&
                <Grid2 container spacing={2}>
                    {
                        hasNoIndustries &&
                        <Grid2 xs={12}>
                            <p className={'text-sm text-color-3 text-center my-5 py-5'}>
                                <WarningOutlined
                                    color={'warning'}
                                    sx={{display: 'inline-block', marginInlineEnd: 0.5}}
                                />
                                There are no industries in your organization. Please add an industry to continue.
                                <IconButton
                                    color={'paper.primary'}
                                    component={Link}
                                    to={routes.settings.industries}
                                    sx={{display: 'inline-block', marginInlineStart: 0.5}}
                                >
                                    <OpenInNew/>
                                </IconButton>
                            </p>
                        </Grid2>
                    }
                    {
                        !hasNoIndustries &&
                        <>
                            <Grid2 xs={12}>
                                <TextField
                                    select
                                    fullWidth
                                    label={'Industry'}
                                    value={fetchedIndustry?.id ?? ''}
                                    onChange={(e) => void fetchIndustry({industryId: e.target.value})}
                                    variant={'filled'}
                                    InputProps={{disableUnderline: true, sx: {borderRadius: 1}}}
                                    SelectProps={{
                                        renderValue: (value) => industries.find((industry) => industry.id === value)?.title ?? '',
                                    }}
                                >
                                    {
                                        industries.map((industry) => (
                                            <MenuItem key={industry.id} value={industry.id}>
                                                {industry.title}
                                            </MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid2>
                            {
                                !fetchedIndustry?.id &&
                                <Grid2 xs={12}>
                                    <p className={'text-sm text-color-3 text-center my-5 py-5'}>
                                        Please select an industry to view its properties.
                                    </p>
                                </Grid2>
                            }
                            {
                                fetchedIndustry?.id &&
                                <Grid2 xs={12}>
                                    <Paper
                                        sx={({
                                            width: '100%',
                                            padding: 2,
                                            gap: 2,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            position: 'relative',
                                            zIndex: 1,
                                            height: 'fit-content',
                                        })}
                                    >
                                        <Box sx={{
                                            flexGrow: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 1,
                                            paddingBlock: 1,
                                            paddingInlineEnd: 0.5,
                                            position: 'relative',
                                        }}>
                                            <Typography variant={'body2'} marginBlockEnd={1}>
                                                You can reorder the properties of the industry by dragging and dropping them. The order of the properties will be reflected in technical information
                                                section of the product page. <br/><br/>
                                                You can also choose to show or hide a property in the lookup results by toggling the checkbox next to the property.
                                            </Typography>
                                            {
                                                (waitingForCommitting.orders || waitingForCommitting.highlighted) &&
                                                <PanelFooter>
                                                    <Typography variant={'body2'} sx={{color: 'text.secondary', marginBlock: 2}}>
                                                        You have made changes to
                                                        the {[waitingForCommitting.orders ? 'order' : '', waitingForCommitting.highlighted ? 'highlighting' : ''].filter(e => e).join(' and ')}. The
                                                        changes will be applied in a few seconds. Please do not close this page.
                                                    </Typography>
                                                </PanelFooter>
                                            }
                                            <ReactSortable
                                                list={properties}
                                                setList={toggleOrder}
                                                sort
                                                disabled={moving || loadingProperties}
                                                handle={'.handle'}
                                                ghostClass={'ghost'}
                                                animation={210}
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    flexGrow: 1,
                                                    ...(!loadingProperties && {
                                                        minHeight: 100,
                                                    }),
                                                }}
                                            >
                                                {
                                                    properties.map((property, index) => (
                                                        <div key={property.id} className={'w-100 py-2'}>
                                                            <Card
                                                                variant={'outlined'}
                                                                sx={{
                                                                    padding: 1,
                                                                    gap: 1,
                                                                    color: 'text.primary',
                                                                    borderColor: 'divider',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    height: 'fit-content',
                                                                    width: 'calc(100%)'
                                                                }}
                                                            >
                                                                <Fade in={!moving}>
                                                                    <IconButton color={'paper.primary'} className={'handle'}>
                                                                        <DragHandleRounded/>
                                                                    </IconButton>
                                                                </Fade>
                                                                <Box
                                                                    sx={{
                                                                        borderRadius: '50%',
                                                                        width: 34,
                                                                        height: 34,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        backgroundColor: 'background.default',
                                                                        color: 'text.primary',
                                                                        cursor: 'pointer',
                                                                    }}
                                                                    onClick={() => openReorderDialog(index)}
                                                                >
                                                                    <Typography variant={'body1'} fontWeight={600}>
                                                                        {index + 1}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant={'body2'} sx={{marginInlineEnd: 'auto'}}>
                                                                    {property.title}
                                                                </Typography>
                                                                <FormControlLabel
                                                                    label={settingHighlighted.includes(property.name) ? 'Processing...' : 'Show in lookup results'}
                                                                    sx={{margin: 0}}
                                                                    labelPlacement={'start'}
                                                                    control={
                                                                        <Checkbox
                                                                            disabled={!!settingHighlighted.length}
                                                                            sx={{paddingBlock: 0.75}}
                                                                            disableRipple
                                                                            checked={property.isHighlighted}
                                                                            onChange={() => toggleHighlighted(property)}
                                                                        />
                                                                    }
                                                                />
                                                            </Card>
                                                        </div>
                                                    ))
                                                }
                                            </ReactSortable>
                                            {
                                                loadingProperties &&
                                                <Grid container spacing={2}>
                                                    {
                                                        Array.from({length: 10}).map((_, key) => (
                                                            <Grid key={`loading-${key}`} xs={12}>
                                                                <Skeleton
                                                                    width={'100%'}
                                                                    sx={{minHeight: 54, transformOrigin: 'none', transform: 'none'}}
                                                                />
                                                            </Grid>
                                                        ))
                                                    }
                                                </Grid>
                                            }
                                        </Box>
                                    </Paper>
                                </Grid2>
                            }
                        </>
                    }
                </Grid2>
            }
            <ChangeOrderDialog
                open={changeOrderDialogState.open}
                close={changeOrderDialogState.close}
                orderIndex={changeOrderDialogState.orderIndex}
                length={changeOrderDialogState.length}
                reorder={changeOrderDialogState.reorder}
                message={changeOrderDialogState.message}
            />
        </>
    );
}

export default ProductPropertiesConfigurationsView;